import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "include", ...init });
  if (!res.ok) {
    let text = "";
    try { text = await res.text(); } catch {}
    throw new Error(`${res.status}${text ? `: ${text}` : ""}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// GET /api/admin/users returns a bare array of user rows:
//   [{ id, email, name, isAdmin, timezone, createdAt }, ...]
export function useAdminUsers() {
  return useQuery({
    queryKey: ["/api/admin/users"],
    queryFn: () => fetchJson<any[]>("/api/admin/users"),
  });
}

export function useUserSubscription(userId: number | null) {
  return useQuery({
    queryKey: ["/api/admin/users", userId, "subscription"],
    queryFn: () => fetchJson<{ subscription: any | null }>(`/api/admin/users/${userId}/subscription`),
    enabled: !!userId,
  });
}

export function useUserLedger(userId: number | null, limit = 100) {
  return useQuery({
    queryKey: ["/api/admin/users", userId, "ledger", limit],
    queryFn: () => fetchJson<{ rows: any[] }>(`/api/admin/users/${userId}/ledger?limit=${limit}`),
    enabled: !!userId,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { email: string; password: string; name: string }) =>
      fetchJson<{ id: number; email: string; name: string }>(`/api/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(args),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/users"] });
    },
  });
}

export function useAssignPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: number; planId: number }) =>
      fetchJson(`/api/admin/users/${args.userId}/assign-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planId: args.planId }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "subscription"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "ledger"] });
    },
  });
}

export function useGrantCredits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: number; amount: number; reason: string }) =>
      fetchJson(`/api/admin/users/${args.userId}/grant-credits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: args.amount, reason: args.reason }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "subscription"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "ledger"] });
    },
  });
}

export function useRevokeCredits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: number; amount: number; reason: string }) =>
      fetchJson(`/api/admin/users/${args.userId}/revoke-credits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: args.amount, reason: args.reason }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "subscription"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "ledger"] });
    },
  });
}

export function useRefundUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: number; reason: string }) =>
      fetchJson(`/api/admin/users/${args.userId}/refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: args.reason }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "subscription"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "ledger"] });
    },
  });
}

// v7.54 — admin "Login as": mints a per-window impersonation token for the user.
export function useImpersonateUser() {
  return useMutation({
    mutationFn: (userId: number) =>
      fetchJson<{ token: string; expiresAt: number; user: { id: number; email: string; name: string | null } }>(
        `/api/admin/users/${userId}/impersonate`,
        { method: "POST" },
      ),
  });
}
